import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bell, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

/**
 * Bell icon + dropdown that lists pending squad invites. Opens a WS connection
 * to /api/ws/notifications on mount so the badge count and invite list stay
 * in sync without polling.
 */
export const NotificationsBell = ({ user, onInviteAccepted }) => {
  const [invites, setInvites] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [handling, setHandling] = useState(null); // invite id being accepted/declined
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invites/pending`);
      setInvites(data.invites || []);
      setPendingCount(data.count || 0);
    } catch (e) {
      logger.error('Failed to fetch pending invites', e);
    }
  }, []);

  const connectWS = useCallback(() => {
    if (!user?.id) return;
    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/notifications`);

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'pending_count') {
            setPendingCount(msg.count);
            if (msg.count > 0) fetchPending();
          } else if (msg.type === 'new_invite') {
            toast.info(`${msg.invite.from_username} invited you to a squad!`);
            fetchPending();
          } else if (msg.type === 'invite_accepted' && onInviteAccepted) {
            // Our outgoing invite was accepted — auto-join the room
            toast.success('Invite accepted! Joining the room…');
            onInviteAccepted(msg.room);
          } else if (msg.type === 'invite_declined') {
            toast.message('Your squad invite was declined');
          }
        } catch (err) {
          logger.error('Bad notification payload', err);
        }
      };

      ws.onclose = () => {
        // Simple reconnect strategy
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWS();
          }, 3000);
        }
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch (e) {
          /* ignore */
        }
      };

      wsRef.current = ws;
    } catch (e) {
      logger.error('Notifications WS failed to open', e);
    }
  }, [user?.id, fetchPending, onInviteAccepted]);

  useEffect(() => {
    fetchPending();
    connectWS();
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (e) {
          /* ignore */
        }
        wsRef.current = null;
      }
    };
  }, [fetchPending, connectWS]);

  const accept = async (invite) => {
    setHandling(invite.id);
    try {
      const { data } = await axios.post(`${API}/invites/${invite.id}/accept`);
      toast.success(`Joined squad with ${invite.from_username}`);
      setOpen(false);
      await fetchPending();
      if (onInviteAccepted) onInviteAccepted(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setHandling(null);
    }
  };

  const decline = async (invite) => {
    setHandling(invite.id);
    try {
      await axios.post(`${API}/invites/${invite.id}/decline`);
      await fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline invite');
    } finally {
      setHandling(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="relative hover:bg-[#F4F4F5]"
          data-testid="notifications-bell-button"
          aria-label={`Notifications (${pendingCount})`}
        >
          <Bell size={18} weight={pendingCount > 0 ? 'fill' : 'regular'} />
          {pendingCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 bg-[#FF3333] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
              data-testid="notifications-badge"
            >
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0 border-[#E4E4E7]"
        data-testid="notifications-panel"
      >
        <div className="px-4 py-3 border-b border-[#E4E4E7]">
          <h4 className="font-heading text-sm font-bold text-[#09090B]">
            Squad Invites {pendingCount > 0 && `(${pendingCount})`}
          </h4>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {invites.length === 0 ? (
            <p
              className="text-sm text-[#71717A] px-4 py-6 text-center"
              data-testid="notifications-empty"
            >
              You&apos;re all caught up.
            </p>
          ) : (
            invites.map((inv) => (
              <div
                key={inv.id}
                className="px-4 py-3 border-b border-[#E4E4E7] last:border-b-0"
                data-testid={`invite-row-${inv.id}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={inv.from_avatar} alt={inv.from_username} />
                    <AvatarFallback>
                      {inv.from_username?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#09090B]">
                      <span className="font-semibold">{inv.from_username}</span>{' '}
                      <span className="text-[#71717A]">invites you to play</span>
                    </p>
                    <p className="text-xs text-[#52525B] font-mono mt-0.5">
                      {inv.game_type}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={() => accept(inv)}
                        disabled={handling === inv.id}
                        size="sm"
                        className="h-7 px-2 bg-[#0047FF] hover:bg-[#0037CC] text-white"
                        data-testid={`accept-invite-${inv.id}`}
                      >
                        <Check size={12} weight="bold" className="mr-1" />
                        Accept
                      </Button>
                      <Button
                        onClick={() => decline(inv)}
                        disabled={handling === inv.id}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 border-[#E4E4E7]"
                        data-testid={`decline-invite-${inv.id}`}
                      >
                        <X size={12} className="mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
